import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const Mesas = () => {
  const { hasPermission } = usePermissions();
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMesa, setEditingMesa] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadMesas = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/mesas');
      if (response.ok) {
        const data = await response.json();
        setMesas(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar las mesas');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las mesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMesas();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mesasFiltradas = mesas.filter(m =>
    !filtroNombre || m.mes_nombre?.toLowerCase().includes(filtroNombre.toLowerCase())
  );
  const mesasPaginadas = mesasFiltradas.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleOpenModal = (mesa = null) => {
    setEditingMesa(mesa);
    if (mesa) {
      form.setFieldsValue({ nombre: mesa.mes_nombre, activo: mesa.mes_activo });
    } else {
      form.resetFields();
      form.setFieldsValue({ activo: true });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingMesa) {
        const response = await apiClient.put(`/gestion/mesas/${editingMesa.mes_id}`, values);
        if (response.ok) {
          message.success('Mesa actualizada correctamente');
          setModalVisible(false);
          loadMesas();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al actualizar la mesa');
        }
      } else {
        const response = await apiClient.post('/gestion/mesas', values);
        if (response.ok) {
          message.success('Mesa creada correctamente');
          setModalVisible(false);
          loadMesas();
        } else {
          const err = await response.json();
          message.error(err.message || 'Error al crear la mesa');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar la mesa');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/mesas/${id}`);
      if (response.ok) {
        message.success('Mesa eliminada correctamente');
        loadMesas();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al eliminar la mesa');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la mesa');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'mes_nombre',
      key: 'mes_nombre',
    },
    {
      title: 'Estado',
      dataIndex: 'mes_activo',
      key: 'mes_activo',
      width: 100,
      align: 'center',
      render: (activo) => (
        <Tag color={activo ? 'success' : 'default'}>
          {activo ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    ...((hasPermission('mesas', 'editar') || hasPermission('mesas', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          {hasPermission('mesas', 'editar') && (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} size="small">
              Editar
            </Button>
          )}
          {hasPermission('mesas', 'eliminar') && (
            <Popconfirm
              title="¿Eliminar esta mesa?"
              onConfirm={() => handleDelete(record.mes_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Mesas
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={loadMesas} loading={loading}>
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('mesas', 'crear') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal(null)}>
                Nueva Mesa
              </Button>
            )}
          </Space>
          <Card style={{ marginBottom: 16, backgroundColor: '#2d2d2d', border: 'none' }}>
            <Input
              placeholder="Buscar por nombre..."
              value={filtroNombre}
              onChange={(e) => {
                setFiltroNombre(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ maxWidth: 300 }}
            />
          </Card>
        </div>

        <Table
          columns={columns}
          dataSource={mesasPaginadas}
          loading={loading}
          rowKey="mes_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: mesasFiltradas.length,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} mesas`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            },
          }}
        />

        <Modal
          title={editingMesa ? 'Editar Mesa' : 'Nueva Mesa'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => { setModalVisible(false); form.resetFields(); }}
          okText="Guardar"
          cancelText="Cancelar"
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'Ingrese el nombre de la mesa' }]}
            >
              <Input placeholder="Ej: Mesa 1, Terraza, Barra" />
            </Form.Item>
            <Form.Item name="activo" label="Activa" valuePropName="checked">
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Mesas;
